import { useContext } from 'react'

import {
  useMediaActionsListApi,
  useRequestAutoArticleCreationMutation
} from '@/common/api/modules/mediaAction'
import { mediaActionPath } from '@/common/app/path'
import { MediaActionType } from '@/common/model/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import Heading from '@/components/view/atoms/heading/Heading'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'

import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

interface Props {
  siteId: number
  mediaAnalysisId: number
}

const AnalysisActionList = ({ siteId, mediaAnalysisId }: Props) => {
  const {
    isLoading,
    data: actionListData,
    mutate
  } = useMediaActionsListApi({
    site_id: siteId,
    source_analytics_id: mediaAnalysisId
  })

  const { showModal: showConfirm, hide: hideConfirm } = useContext(ConfirmDialogContext)
  const { trigger: requestAutoArticleCreation, isMutating: requestingAutoCreate } =
    useRequestAutoArticleCreationMutation({
      onSuccess: () => {
        hideConfirm()
        mutate()
      }
    })

  const handleAutoCreate = (actionId: number) =>
    showConfirm({
      message:
        'この推奨アクションから記事の自動作成を予約します。バックグラウンドで数分以内に処理され、他の操作は完了までできなくなります。よろしいですか？',
      noText: 'キャンセル',
      yesText: '自動記事作成を予約',
      onClickYes: async () => {
        await requestAutoArticleCreation({ site_id: siteId, action_id: actionId })
      }
    })

  const mediaActionPathWithSource = mediaActionPath(
    siteId,
    mediaAnalysisId ? { sourceAnalysisId: mediaAnalysisId } : undefined
  )

  return (
    <>
      {/* 推奨アクション */}
      {isLoading ? (
        <PageListLoading />
      ) : (
        actionListData &&
        actionListData.items.length > 0 && (
          <Area bottomMargin={16}>
            <Heading
              subText='本分析から生成された推奨アクション。実行・承認・自動化は推奨アクション管理で行います。'
              rightNode={
                <TextButton link={mediaActionPathWithSource} arrow>
                  推奨アクション管理
                </TextButton>
              }
            >
              推奨アクション
            </Heading>
            <AreaContent>
              <div className='flex flex-col gap-2'>
                {actionListData.items.map((action) => (
                  <PriorityBar
                    key={action.id}
                    priority={action.priority}
                    title={action.title}
                    contentText={action.description}
                    subContent={
                      <>
                        <div className='flex w-70 flex-col gap-1'>
                          <Badge size={10} badgeType={BadgeType.SQUARE_TERTIARY}>
                            対象キーワード
                          </Badge>
                          <ItemAlign gapX={4} gapY={4} align='center' direction='horizon'>
                            {action.target_keywords.map((kw, ki) => (
                              <Badge key={ki} size={10} badgeType={BadgeType.KEYWORD}>
                                {kw}
                              </Badge>
                            ))}
                          </ItemAlign>
                        </div>
                        {action.action_type === MediaActionType.CREATE_ARTICLE && (
                          <div className='shrink-0'>
                            <AiButton
                              loading={
                                action.auto_article_creation_requested && requestingAutoCreate
                              }
                              onClick={() => handleAutoCreate(action.id)}
                            >
                              自動記事作成
                            </AiButton>
                          </div>
                        )}
                      </>
                    }
                  />
                ))}
              </div>
            </AreaContent>
          </Area>
        )
      )}
    </>
  )
}

export default AnalysisActionList
